import React, { useState, useEffect } from 'react';
import { Gem, Package, TrendingUp, ShoppingCart, FileText, ArrowRight, ArrowUp, ArrowDown, AlertTriangle, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import PageIdentifier from './PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';

const SECTION_IDS = ['inventory', 'gold-schemes', 'sales', 'quick-actions'];

const DashboardSection = ({ title, icon: Icon, iconClassName, badge, badgeClassName, collapsed, onToggle, onMoveUp, onMoveDown, children }) => (
  <section className="mb-6 bg-white rounded-xl shadow-lg overflow-hidden">
    <div className="flex items-center gap-1 pr-3">
      <h2 className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="w-full flex items-center gap-3 px-4 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
        >
          <ChevronDown className={`shrink-0 text-gray-500 transition-transform ${collapsed ? '-rotate-90' : ''}`} size={22} />
          <Icon className={`shrink-0 ${iconClassName}`} size={24} />
          <span className="truncate text-xl font-semibold text-gray-800">{title}</span>
          {badge && (
            <span className={`shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${badgeClassName}`}>{badge}</span>
          )}
        </button>
      </h2>
      <button
        type="button"
        onClick={onMoveUp}
        disabled={!onMoveUp}
        aria-label={`Move ${title} up`}
        title="Move up"
        className="shrink-0 p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <ArrowUp size={18} />
      </button>
      <button
        type="button"
        onClick={onMoveDown}
        disabled={!onMoveDown}
        aria-label={`Move ${title} down`}
        title="Move down"
        className="shrink-0 p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <ArrowDown size={18} />
      </button>
    </div>
    {!collapsed && <div className="px-4 sm:px-6 pb-6 space-y-8">{children}</div>}
  </section>
);

const Dashboard = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canViewStockAlerts = hasPermission('dashboard', 'edit');
  const [loading, setLoading] = useState(true);
  const [schemeAnalytics, setSchemeAnalytics] = useState(null);
  const [overduePayments, setOverduePayments] = useState([]);
  const [stats, setStats] = useState({
    totalAvailable: 0,
    totalSold: 0,
    totalReturn: 0,
    totalCostValue: 0,
    totalSaleValue: 0,
    categoryBreakdown: [],
    recentItems: [],
    salesTrend: {
      thisWeek: 0,
      lastWeek: 0,
      thisMonth: 0
    }
  });
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [layout, setLayout] = useState({ order: SECTION_IDS, collapsed: [] });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch inventory statistics
        const response = await fetch('/api/reports/dashboard', {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setStats(data.data);
          }
        }

        // Fetch scheme analytics
        try {
          const schemeResponse = await fetch('/api/schemes/analytics/summary', {
            credentials: 'include'
          });
          if (schemeResponse.ok) {
            const schemeData = await schemeResponse.json();
            if (schemeData.success) {
              setSchemeAnalytics(schemeData.data);
            }
          }
        } catch (error) {
          console.error('Error fetching scheme analytics:', error);
        }

        if (canViewStockAlerts) {
          try {
            const alertsResponse = await fetch('/api/inventory/low-stock-alerts', { credentials: 'include' });
            if (alertsResponse.ok) {
              const alertsData = await alertsResponse.json();
              if (alertsData.success) setLowStockAlerts(alertsData.data);
            }
          } catch (error) {
            console.error('Error fetching low-stock alerts:', error);
          }
        }

        // Fetch overdue payments
        try {
          const overdueResponse = await fetch('/api/schemes/payments/overdue', {
            credentials: 'include'
          });
          if (overdueResponse.ok) {
            const overdueData = await overdueResponse.json();
            if (overdueData.success && Array.isArray(overdueData.data)) {
              setOverduePayments(overdueData.data);
            } else if (Array.isArray(overdueData)) {
              setOverduePayments(overdueData);
            }
          }
        } catch (error) {
          console.error('Error fetching overdue payments:', error);
        }

        // Fetch this user's saved section order and minimized sections
        try {
          const layoutResponse = await fetch('/api/users/me/dashboard-layout', { credentials: 'include' });
          if (layoutResponse.ok) {
            const layoutData = await layoutResponse.json();
            if (layoutData.success) {
              const { order = [], collapsed = [] } = layoutData.data;
              setLayout({
                order: [...new Set([...order, ...SECTION_IDS])].filter(id => SECTION_IDS.includes(id)),
                collapsed
              });
            }
          }
        } catch (error) {
          console.error('Error fetching dashboard layout:', error);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [canViewStockAlerts]);

  const saveLayout = async (nextLayout) => {
    setLayout(nextLayout);
    try {
      await fetch('/api/users/me/dashboard-layout', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nextLayout)
      });
    } catch (error) {
      console.error('Error saving dashboard layout:', error);
    }
  };

  const toggleSection = (id) => {
    const collapsed = layout.collapsed.includes(id)
      ? layout.collapsed.filter(sectionId => sectionId !== id)
      : [...layout.collapsed, id];
    saveLayout({ ...layout, collapsed });
  };

  // Swaps with the neighbouring visible section, so a section hidden for lack
  // of data can't make a move look like it did nothing
  const swapSections = (id, neighbourId) => {
    const order = layout.order.map(sectionId => {
      if (sectionId === id) return neighbourId;
      if (sectionId === neighbourId) return id;
      return sectionId;
    });
    saveLayout({ ...layout, order });
  };

  if (loading) {
    return (
      <div className="p-6 pb-12">
        <PageIdentifier pageId={SCREEN_IDS?.DASHBOARD?.MAIN || 'DASH-001'} pageName="Main Dashboard" />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const sections = {
    inventory: {
      title: 'Inventory',
      icon: Package,
      iconClassName: 'text-blue-600',
      badge: lowStockAlerts.length > 0 ? `${lowStockAlerts.length} low stock` : null,
      badgeClassName: 'bg-amber-200 text-amber-900',
      content: (
        <>
          {/* Main Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Items in Inventory */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total Items</p>
                  <p className="text-4xl font-bold mt-2">{stats.totalAvailable}</p>
                  <p className="text-blue-100 text-xs mt-1">Existing items only</p>
                </div>
                <Package className="text-blue-200" size={48} />
              </div>
            </div>

            {/* Total Sold */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Items Sold</p>
                  <p className="text-4xl font-bold mt-2">{stats.totalSold}</p>
                  <p className="text-green-100 text-xs mt-1">Sold today</p>
                </div>
                <ShoppingCart className="text-green-200" size={48} />
              </div>
            </div>

          </div>

          {/* Low Stock Alerts */}
          {canViewStockAlerts && lowStockAlerts.length > 0 && (
            <div>
              <div className="overflow-hidden rounded-lg border border-amber-200 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-4 border-b border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <AlertTriangle size={18} className="shrink-0 text-amber-600" />
                    <h2 className="truncate font-semibold text-amber-900">Low Stock Alerts</h2>
                  </div>
                  <span className="shrink-0 rounded-full bg-amber-200 px-2 py-0.5 text-xs font-bold text-amber-900">
                    {lowStockAlerts.length}
                  </span>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_5rem_7rem] border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase text-gray-500">
                  <span>Category</span>
                  <span className="text-right">Stock</span>
                  <span className="text-right">Status</span>
                </div>
                <div className="max-h-72 divide-y divide-gray-100 overflow-y-auto">
                  {lowStockAlerts.map(alert => {
                    const isOutOfStock = Number(alert.stock_count) === 0;

                    return (
                      <div
                        key={alert.category_id}
                        className="grid grid-cols-[minmax(0,1fr)_5rem_7rem] items-center px-4 py-2.5 text-sm"
                      >
                        <span className="truncate font-medium text-gray-800" title={alert.category_name}>
                          {alert.category_name}
                        </span>
                        <span className={`text-right font-bold ${isOutOfStock ? 'text-red-700' : 'text-amber-700'}`}>
                          {alert.stock_count}
                        </span>
                        <span className={`text-right text-xs font-semibold ${isOutOfStock ? 'text-red-700' : 'text-amber-700'}`}>
                          {isOutOfStock ? 'Out of stock' : 'Low stock'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Category Breakdown & Recent Items */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Stock by Category */}
            <div className="p-5 rounded-xl border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <Gem className="mr-2 text-blue-600" size={24} />
                Available Stock by Category
              </h3>

              <div className="space-y-3">
                {stats.categoryBreakdown && stats.categoryBreakdown.length > 0 ? (
                  stats.categoryBreakdown.map((category, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${index % 5 === 0 ? 'bg-blue-500' :
                          index % 5 === 1 ? 'bg-green-500' :
                            index % 5 === 2 ? 'bg-purple-500' :
                              index % 5 === 3 ? 'bg-amber-500' :
                                'bg-pink-500'
                          }`}></div>
                        <span className="font-bold text-gray-800">{category.category_name || category.category || 'Uncategorized'}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-gray-800">{category.count}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="mx-auto mb-2 text-gray-400" size={48} />
                    <p>No categories found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Items */}
            <div className="p-5 rounded-xl border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <Package className="mr-2 text-green-600" size={24} />
                Recently Added Items
              </h3>

              <div className="space-y-3">
                {stats.recentItems && stats.recentItems.length > 0 ? (
                  stats.recentItems.slice(0, 5).map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all">
                      <div>
                        <p className="font-semibold text-gray-800">{item.code}</p>
                        <p className="text-sm text-gray-600">{item.category}</p>
                        <p className="text-xs text-gray-500">{item.purity} • {parseFloat(item.gross_weight).toFixed(2)}g</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-xs px-2 py-1 rounded-full ${item.status === 'In Stock' ? 'bg-green-100 text-green-700' :
                          item.status === 'Sold' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                          {item.status}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="mx-auto mb-2 text-gray-400" size={48} />
                    <p>No recent items</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )
    },
    'gold-schemes': {
      title: 'Gold Schemes',
      icon: Gem,
      iconClassName: 'text-yellow-600',
      badge: overduePayments.length > 0 ? `${overduePayments.length} overdue` : null,
      badgeClassName: 'bg-red-500 text-white',
      content: (schemeAnalytics || overduePayments.length > 0) && (
        <>
          {/* Gold Schemes Overview */}
          {schemeAnalytics && (
            <div>
              {/* Top row: scheme count + enrollment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                {/* Total Schemes */}
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-xl shadow-lg border border-yellow-200">
                  <p className="text-gray-600 text-sm font-medium">Total Schemes</p>
                  <p className="text-4xl font-bold text-yellow-700 mt-2">{schemeAnalytics.schemes?.total || 0}</p>
                  <p className="text-xs text-yellow-600 mt-2">Active schemes available</p>
                </div>

                {/* Active Enrollments */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-lg border border-green-200">
                  <p className="text-gray-600 text-sm font-medium">Active Enrollments</p>
                  <p className="text-4xl font-bold text-green-700 mt-2">{schemeAnalytics.enrollments?.active || 0}</p>
                  <p className="text-xs text-green-600 mt-2">Total: {schemeAnalytics.enrollments?.total || 0}</p>
                </div>
              </div>

              {/* INR schemes row */}
              <div className="mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-400"></span>
                  INR Schemes (₹)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl shadow border border-blue-200">
                    <p className="text-gray-600 text-sm font-medium">Collected (INR)</p>
                    <p className="text-3xl font-bold text-blue-700 mt-2">
                      ₹{parseFloat(schemeAnalytics.financials?.inr?.totalPaid || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-xs text-blue-500 mt-1">
                      of ₹{parseFloat(schemeAnalytics.financials?.inr?.totalDue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} due
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-5 rounded-xl shadow border border-orange-200">
                    <p className="text-gray-600 text-sm font-medium">Outstanding (INR)</p>
                    <p className="text-3xl font-bold text-orange-700 mt-2">
                      ₹{parseFloat(schemeAnalytics.financials?.inr?.totalPending || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-xs text-orange-500 mt-1">Pending collection</p>
                  </div>
                </div>
              </div>

              {/* USD schemes row */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-violet-400"></span>
                  USD Schemes ($)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-violet-50 to-violet-100 p-5 rounded-xl shadow border border-violet-200">
                    <p className="text-gray-600 text-sm font-medium">Collected (USD)</p>
                    <p className="text-3xl font-bold text-violet-700 mt-2">
                      ${parseFloat(schemeAnalytics.financials?.usd?.totalPaid || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-xs text-violet-500 mt-1">
                      of ${parseFloat(schemeAnalytics.financials?.usd?.totalDue || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} due
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-red-100 p-5 rounded-xl shadow border border-red-200">
                    <p className="text-gray-600 text-sm font-medium">Outstanding (USD)</p>
                    <p className="text-3xl font-bold text-red-700 mt-2">
                      ${parseFloat(schemeAnalytics.financials?.usd?.totalPending || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-xs text-red-500 mt-1">Pending collection</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Overdue Payments Alert */}
          {overduePayments.length > 0 && (() => {
            const usdPayments = [...overduePayments].filter(p => p.currency === 'USD').sort((a, b) => b.days_overdue - a.days_overdue);
            const inrPayments = [...overduePayments].filter(p => p.currency !== 'USD').sort((a, b) => b.days_overdue - a.days_overdue);

            const SectionRow = ({ label, count }) => (
              <tr>
                <td colSpan="6" className="px-4 py-2 bg-amber-100 border-y border-amber-300 text-amber-900 font-bold text-xs uppercase tracking-wide">
                  {label} <span className="ml-1 text-amber-600 font-normal">({count} overdue)</span>
                </td>
              </tr>
            );

            const PaymentRow = ({ p, i }) => {
              const isUSD = p.currency === 'USD';
              const symbol = isUSD ? '$' : '₹';
              const locale = isUSD ? 'en-US' : 'en-IN';
              return (
                <tr key={p.id || i} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{p.customer_name}</td>
                  <td className="px-4 py-3 text-gray-600">{p.customer_phone || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{p.scheme_name}</td>
                  <td className="px-4 py-3 text-gray-600">{new Date(p.due_date).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-red-600 font-semibold text-xs">{p.days_overdue} days</span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-800">
                    {symbol}{parseFloat(p.payment_amount || 0).toLocaleString(locale, { maximumFractionDigits: 0 })}
                  </td>
                </tr>
              );
            };

            return (
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <AlertTriangle className="mr-2 text-red-500" size={24} />
                  Overdue Payments
                  <span className="ml-3 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                    {overduePayments.length}
                  </span>
                </h3>
                <div className="bg-white border border-amber-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-100 border-b-2 border-amber-300">
                        <tr>
                          <th className="text-left px-4 py-3 font-bold text-amber-950 uppercase tracking-wide text-xs">Customer</th>
                          <th className="text-left px-4 py-3 font-bold text-amber-950 uppercase tracking-wide text-xs">Phone</th>
                          <th className="text-left px-4 py-3 font-bold text-amber-950 uppercase tracking-wide text-xs">Scheme</th>
                          <th className="text-left px-4 py-3 font-bold text-amber-950 uppercase tracking-wide text-xs">Due Date</th>
                          <th className="text-right px-4 py-3 font-bold text-amber-950 uppercase tracking-wide text-xs">Days Overdue</th>
                          <th className="text-right px-4 py-3 font-bold text-amber-950 uppercase tracking-wide text-xs">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usdPayments.length > 0 && (
                          <>
                            <SectionRow label="USD Scheme" count={usdPayments.length} />
                            {usdPayments.map((p, i) => <PaymentRow key={p.id || i} p={p} i={i} />)}
                          </>
                        )}
                        {inrPayments.length > 0 && (
                          <>
                            <SectionRow label="INR Scheme" count={inrPayments.length} />
                            {inrPayments.map((p, i) => <PaymentRow key={p.id || i} p={p} i={i} />)}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      )
    },
    sales: {
      title: 'Sales Trends',
      icon: TrendingUp,
      iconClassName: 'text-blue-600',
      content: (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-600 font-medium">This Week</p>
            <p className="text-3xl font-bold text-blue-700 mt-2">{stats.salesTrend.thisWeek}</p>
            <p className="text-xs text-blue-500 mt-1">items sold</p>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <p className="text-sm text-green-600 font-medium">Last Week</p>
            <p className="text-3xl font-bold text-green-700 mt-2">{stats.salesTrend.lastWeek}</p>
            <p className="text-xs text-green-500 mt-1">items sold</p>
          </div>

          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <p className="text-sm text-purple-600 font-medium">This Month</p>
            <p className="text-3xl font-bold text-purple-700 mt-2">{stats.salesTrend.thisMonth}</p>
            <p className="text-xs text-purple-500 mt-1">items sold</p>
          </div>
        </div>
      )
    },
    'quick-actions': {
      title: 'Quick Actions',
      icon: ArrowRight,
      iconClassName: 'text-indigo-600',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'indian-billing' }))}
            className="w-full flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors group"
            title={`Screen ID: ${SCREEN_IDS.BILLING.INDIAN_BILLING}`}
          >
            <div className="flex items-center">
              <FileText className="text-blue-600 mr-3" size={20} />
              <span className="font-medium text-gray-700">Indian Billing</span>
            </div>
            <ArrowRight className="text-blue-600 group-hover:translate-x-1 transition-transform" size={18} />
          </button>

          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'us-billing' }))}
            className="w-full flex items-center justify-between p-3 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors group"
            title={`Screen ID: ${SCREEN_IDS.BILLING.US_BILLING}`}
          >
            <div className="flex items-center">
              <FileText className="text-indigo-600 mr-3" size={20} />
              <span className="font-medium text-gray-700">US Billing</span>
            </div>
            <ArrowRight className="text-indigo-600 group-hover:translate-x-1 transition-transform" size={18} />
          </button>

          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'inr-estimate' }))}
            className="w-full flex items-center justify-between p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors group"
            title={`Screen ID: ${SCREEN_IDS.ESTIMATES.INR_ESTIMATE}`}
          >
            <div className="flex items-center">
              <FileText className="text-green-600 mr-3" size={20} />
              <span className="font-medium text-gray-700">INR Estimate</span>
            </div>
            <ArrowRight className="text-green-600 group-hover:translate-x-1 transition-transform" size={18} />
          </button>

          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'usd-estimate' }))}
            className="w-full flex items-center justify-between p-3 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors group"
            title={`Screen ID: ${SCREEN_IDS.ESTIMATES.USD_ESTIMATE}`}
          >
            <div className="flex items-center">
              <FileText className="text-teal-600 mr-3" size={20} />
              <span className="font-medium text-gray-700">USD Estimate</span>
            </div>
            <ArrowRight className="text-teal-600 group-hover:translate-x-1 transition-transform" size={18} />
          </button>

          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'view-inventory' }))}
            className="w-full flex items-center justify-between p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors group"
            title={`Screen ID: ${SCREEN_IDS.INVENTORY.VIEW_LIST}`}
          >
            <div className="flex items-center">
              <Package className="text-purple-600 mr-3" size={20} />
              <span className="font-medium text-gray-700">View Inventory</span>
            </div>
            <ArrowRight className="text-purple-600 group-hover:translate-x-1 transition-transform" size={18} />
          </button>
        </div>
      )
    }
  };

  const visibleSectionIds = layout.order.filter(id => sections[id].content);

  return (
    <div className="p-6 pb-12 bg-gray-50 min-h-screen">
      <PageIdentifier pageId={SCREEN_IDS?.DASHBOARD?.MAIN || 'DASH-001'} pageName="Main Dashboard" />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome back, {user?.firstName || user?.username || 'User'}! Here's your inventory overview.</p>
      </div>

      {visibleSectionIds.map((id, index) => {
        const { content, ...section } = sections[id];
        const previousId = visibleSectionIds[index - 1];
        const nextId = visibleSectionIds[index + 1];

        return (
          <DashboardSection
            key={id}
            {...section}
            collapsed={layout.collapsed.includes(id)}
            onToggle={() => toggleSection(id)}
            onMoveUp={previousId ? () => swapSections(id, previousId) : undefined}
            onMoveDown={nextId ? () => swapSections(id, nextId) : undefined}
          >
            {content}
          </DashboardSection>
        );
      })}
    </div>
  );
};

export { Dashboard };